import OpenInNew from "@mui/icons-material/OpenInNew";
import LocalOffer from "@mui/icons-material/LocalOffer";
import RestartAlt from "@mui/icons-material/RestartAlt";
import ReportProblem from "@mui/icons-material/ReportProblem";
import SignalCellularAlt from "@mui/icons-material/SignalCellularAlt";
import SignalCellularAlt2Bar from "@mui/icons-material/SignalCellularAlt2Bar";
import SignalCellularAlt1Bar from "@mui/icons-material/SignalCellularAlt1Bar";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { getActivityTitleOnly } from "encamp-shared/src/activity/activityDisplayNames";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityType as PrismaActivityType } from "encamp-prisma/generated/client";
import { ActivityStatus } from "generated-graphql/graphql";
import { useQuery, useMutation } from "@apollo/client";
import { WORKFLOW_ACTIVITIES_QUERY } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/api";
import {
  ERROR_CLUSTER_RUN_ID_KEY,
  ERROR_CLUSTER_GROUP_ID_KEY,
} from "encamp-omnisearch/src/constants/keyNames";
import React, { useMemo, useState, useCallback, createContext, useContext, Dispatch, SetStateAction } from "react";
import { ErrorGroupWorkflowTagsCell } from "routes/Staff/ErrorClustering/ErrorGroupWorkflowTagsCell";
import { useLinearTags } from "hooks/useLinearTags";
import type {
  WorkflowActivitiesQuery,
  WorkflowActivitiesQueryVariables,
} from "generated-graphql/graphql";
import { UPDATE_WORKFLOW_TAGS_BULK_MUTATION } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/ManageWorkflowTags";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import { EntityUpdate } from "components/ManageTags/types";
import { useRequeueActivities } from "hooks/activities/requeue";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { FormProvider, useForm } from "react-hook-form";
import { compact } from "lodash-es";
import { LinearIcon } from "components/LinearIcon";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY } from "./index";
import { WorkflowActivitiesRow } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/types";

type WorkflowRow = NonNullable<
  NonNullable<WorkflowActivitiesQuery["workflowActivities"]>["items"]
>[number];

interface ErrorGroupDetailPanelProps {
  runId: string;
  groupId: number;
}

const CREATE_LINEAR_ISSUE_MUTATION = gql(`
  mutation CreateLinearIssue($input: CreateLinearIssueInput!) {
    createLinearIssue(input: $input) {
      success
      issueIdentifier
      issueUrl
      issueId
    }
  }
`);

// Context for bulk actions (similar to BulkActionsMenu)
type DialogState =
  | "manageWorkflowTags"
  | "requeue"
  | null;

// Create a context that matches the BulkActionsMenu context structure
const bulkActionContext = createContext<{
  selectedRows: WorkflowActivitiesRow[];
  dialogState: DialogState;
  setDialogState: Dispatch<SetStateAction<DialogState>>;
  onClose: () => void;
  clearSelection: () => void;
}>({
  selectedRows: [],
  dialogState: null,
  setDialogState: () => {},
  onClose: () => {},
  clearSelection: () => {},
});

// Export a hook that matches the BulkActionsMenu hook name so existing components can use it
export function useBulkActionContext() {
  return useContext(bulkActionContext);
}

// Wrapper components that reuse existing dialog components but use our context
function ManageWorkflowTagsDialogWrapper() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows, clearSelection } = useBulkActionContext();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [updateWorkflowTags, { loading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY, WORKFLOW_ACTIVITIES_QUERY],
    }
  );

  const transformedRows: Array<{ id: string; tags?: string[] }> = useMemo(
    () =>
      selectedRows
        .filter((row) => row.workflowId)
        .map((row) => ({
          id: row.workflowId ?? "",
          tags: row.tags || [],
        })),
    [selectedRows]
  );

  const handleSubmit = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));

        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates,
            },
          });
        }

        alerts.success(
          `Successfully updated tags for ${workflowUpdates.length} ${pluralize(
            "workflow",
            workflowUpdates.length
          )}`
        );

        clearSelection();
        onClose();
      } catch (err) {
        alerts.error("An error occurred while updating the workflow tags", err);
      }
    },
    [updateWorkflowTags, alerts, onClose, clearSelection]
  );

  return (
    <ManageTagsFormDialog
      selectedRows={transformedRows}
      getEntityId={(row) => row.id}
      onClose={() => {
        setShowConfirmation(false);
        onClose();
      }}
      onCloseConfirmation={() => setShowConfirmation(false)}
      confirm={() => setShowConfirmation(true)}
      open={dialogState === "manageWorkflowTags"}
      withConfirmation={true}
      showConfirmation={showConfirmation}
      onSubmit={handleSubmit}
      loading={loading}
      entityName="Workflow"
      localStorageKey="workflow-recent-tags"
      commonTags={["compliance", "engineering", "linear"]}
      TagPickerComponent={WorkflowTagPickerAdapter}
    />
  );
}

function RequeueDialogWrapper() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows, clearSelection } = useBulkActionContext();
  const [requeueMutation, { loading }] = useRequeueActivities();
  const form = useForm<{ comment: string | null }>({
    defaultValues: { comment: "" },
    mode: "onChange",
  });
  const { handleSubmit, reset } = form;

  const activityIds = useMemo(
    () => compact(selectedRows.map((row) => row.activityId)),
    [selectedRows]
  );

  const handleClose = useCallback(() => {
    reset({ comment: "" });
    onClose();
  }, [onClose, reset]);

  const onSubmit = handleSubmit(async ({ comment }) => {
    if (!activityIds.length) {
      handleClose();
      return;
    }

    const trimmedComment = (comment ?? "").trim();
    try {
      const res = await requeueMutation({
        variables: {
          activityIds,
          comment: trimmedComment ? trimmedComment : undefined,
        },
      });

      const updatedCount = res.data?.requeueActivities?.updatedCount ?? 0;

      if (activityIds.length === updatedCount) {
        alerts.success("Successfully requeued activities!");
      } else if (updatedCount === 0) {
        alerts.error("No activities were requeued.");
      } else {
        alerts.info(
          "Some activities were requeued, but some failed. Refresh to investigate"
        );
      }
      clearSelection();
      handleClose();
    } catch (error) {
      alerts.error("Failed to requeue activities.", error);
    }
  });

  return (
    <Dialog
      open={dialogState === "requeue"}
      onClose={handleClose}
      title="Requeue Activities"
      primaryActionButtonProps={{
        onClick: onSubmit,
        loading,
        loadingText: "Requeuing...",
        disabled: loading || activityIds.length === 0,
        saveText: "Requeue",
      }}
    >
      <FormProvider {...form}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: "400px",
          }}
        >
          <Typography>
            You are about to requeue {activityIds.length}{" "}
            {activityIds.length === 1 ? "activity" : "activities"}.
          </Typography>
          <FormTextField<{ comment: string | null }>
            name="comment"
            label="Reason (optional)"
            maxLength={400}
            textFieldProps={{
              multiline: true,
              minRows: 6,
              placeholder:
                "Add context for why these activities are being requeued",
            }}
          />
        </Box>
      </FormProvider>
    </Dialog>
  );
}


export const ErrorGroupDetailPanel = React.memo(function ErrorGroupDetailPanel({
  runId,
  groupId,
}: ErrorGroupDetailPanelProps) {
  // Selection state
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });
  
  // Dialog state for bulk actions context
  const [dialogState, setDialogState] = useState<DialogState>(null);
  
  // Linear dialog state (not using context)
  const [linearDialogOpen, setLinearDialogOpen] = useState(false);
  const [linearTicketTitle, setLinearTicketTitle] = useState("");
  const [linearTicketStatus, setLinearTicketStatus] = useState("Triage");
  const [linearTicketPriority, setLinearTicketPriority] = useState(0);
  
  const alerts = useAlerts();
  
  // Mutation for Linear
  const [createLinearIssue, { loading: createLinearLoading }] = useMutation(
    CREATE_LINEAR_ISSUE_MUTATION
  );
  const [updateWorkflowTags] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY, WORKFLOW_ACTIVITIES_QUERY],
    }
  );
  
  // Lazy load workflows only when detail panel is expanded
  const { data, loading } = useQuery<
    WorkflowActivitiesQuery,
    WorkflowActivitiesQueryVariables
  >(WORKFLOW_ACTIVITIES_QUERY, {
    variables: {
      search: `${ERROR_CLUSTER_RUN_ID_KEY}:${runId} ${ERROR_CLUSTER_GROUP_ID_KEY}:${groupId}`,
      page: 0,
      pageSize: 10000, // Get all workflows for this group
    },
    skip: !runId || groupId === undefined,
  });

  // Get workflows list
  const workflows = useMemo(() => {
    return data?.workflowActivities?.items ?? [];
  }, [data]);
  
  // Get selected workflows
  const selectedWorkflows = useMemo(() => {
    const selectedIds = rowSelectionModel.type === "include" 
      ? Array.from(rowSelectionModel.ids)
      : [];
    return workflows.filter((w) => selectedIds.includes(w.id));
  }, [workflows, rowSelectionModel]);
  
  // Transform selected workflows to WorkflowActivitiesRow format for bulk actions
  const selectedRowsAsWorkflowActivities: WorkflowActivitiesRow[] = useMemo(() => {
    return selectedWorkflows.map((w) => ({
      ...w,
      // Ensure all required fields are present
    })) as WorkflowActivitiesRow[];
  }, [selectedWorkflows]);
  
  const handleClose = useCallback(() => {
    setDialogState(null);
  }, []);
  
  const handleClearSelection = useCallback(() => {
    setRowSelectionModel({ type: "include", ids: new Set() });
  }, []);

  // Collect all tags for Linear integration
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    workflows.forEach((workflow) => {
      workflow.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet);
  }, [workflows]);

  const { statusMap: linearStatusMap } = useLinearTags(allTags);
  
  // Bulk action handlers - use dialogState from context
  const handleOpenTagsDialog = useCallback(() => {
    setDialogState("manageWorkflowTags");
  }, [setDialogState]);
  
  const handleOpenRequeueDialog = useCallback(() => {
    setDialogState("requeue");
  }, [setDialogState]);
  
  // Linear ticket handlers
  const handleOpenLinearDialog = useCallback(() => {
    // Build default title from selected workflows
    const workflowCount = selectedWorkflows.length;
    const states = [...new Set(selectedWorkflows.map((w) => w.state).filter(Boolean))].sort();
    const statesDisplay = states.length <= 3 ? states.join(", ") : "Multiple States";
    const defaultTitle = `[${workflowCount}: ${statesDisplay}] Fulfillment Error: Selected Workflows`;
    setLinearTicketTitle(defaultTitle);
    setLinearTicketStatus("Triage");
    setLinearTicketPriority(0);
    setLinearDialogOpen(true);
  }, [selectedWorkflows]);
  
  const handleCloseLinearDialog = useCallback(() => {
    setLinearDialogOpen(false);
    setLinearTicketTitle("");
    setLinearTicketStatus("Triage");
    setLinearTicketPriority(0);
  }, []);
  
  const handleCreateLinearTicket = useCallback(async () => {
    if (!selectedWorkflows.length) return;

    // Build description
    const workflowLinks = selectedWorkflows
      .map((w) => {
        if (w.tierIIReportId) {
          return `- ${w.name} (${w.state}): ${window.location.origin}/staff/fulfillment/r/${w.tierIIReportId}`;
        }
        return `- ${w.name} (${w.state})`;
      })
      .join("\n");
    
    const states = [...new Set(selectedWorkflows.map((w) => w.state).filter(Boolean))].sort();
    const description = [
      `**Selected Workflows**: ${selectedWorkflows.length}`,
      "",
      `**States**: ${states.join(", ")}`,
      "",
      `**Workflows**:`,
      workflowLinks,
    ].join("\n");

    try {
      const result = await createLinearIssue({
        variables: {
          input: {
            title: linearTicketTitle,
            description,
            status: linearTicketStatus,
            priority: linearTicketPriority,
          },
        },
      });

      const issueIdentifier = result.data?.createLinearIssue?.issueIdentifier;
      const issueUrl = result.data?.createLinearIssue?.issueUrl;

      if (!issueIdentifier) {
        throw new Error("Failed to get issue identifier from Linear");
      }

      alerts.success(`Linear ticket ${issueIdentifier} created successfully!`);

      // Tag all selected workflows with the ticket ID and "engineering" tag
      const workflowUpdates = selectedWorkflows.map((workflow) => ({
        workflowId: workflow.workflowId,
        tagNames: [
          ...new Set([...(workflow.tags ?? []), issueIdentifier, "engineering"]),
        ],
      }));

      if (workflowUpdates.length > 0) {
        await updateWorkflowTags({
          variables: {
            workflowUpdates,
          },
        });

        alerts.success(
          `Tagged ${workflowUpdates.length} ${pluralize(
            "workflow",
            workflowUpdates.length
          )} with ${issueIdentifier}`
        );
      }

      // Open the issue in a new tab
      if (issueUrl) {
        window.open(issueUrl, "_blank", "noopener");
      }

      handleCloseLinearDialog();
      setRowSelectionModel({ type: "include", ids: new Set() });
    } catch (err) {
      alerts.error("Failed to create Linear ticket", err);
    }
  }, [
    selectedWorkflows,
    createLinearIssue,
    updateWorkflowTags,
    handleCloseLinearDialog,
    linearTicketTitle,
    linearTicketStatus,
    linearTicketPriority,
    alerts,
  ]);
  
  // Check if requeue is available (workflows with activityId)
  const hasRequeueableWorkflows = useMemo(() => {
    return selectedWorkflows.some((w) => Boolean(w.activityId));
  }, [selectedWorkflows]);

  const columns: GridColDef<WorkflowRow>[] = useMemo(
    () => [
      {
        field: "organizationName",
        headerName: "Org",
        width: 120,
        renderCell: ({ row }) => (
          <Typography variant="body2">{row.organizationName}</Typography>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        width: 200,
        renderCell: ({ row }) => (
          <Typography variant="body2">{row.name}</Typography>
        ),
      },
      {
        field: "state",
        headerName: "State",
        width: 55,
        renderCell: ({ row }) => (
          <Typography variant="body2">{row.state}</Typography>
        ),
      },
      {
        field: "activityType",
        headerName: "Activity",
        width: 120,
        renderCell: ({ row }) => {
          if (row.activityType) {
            return (
              <Typography variant="body2">
                {getActivityTitleOnly(
                  {
                    type: row.activityType as PrismaActivityType,
                    description: row.description ?? null,
                  },
                  undefined,
                  undefined
                )}
              </Typography>
            );
          }
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        },
      },
      {
        field: "activityStatus",
        headerName: "Activity Status",
        width: 115,
        renderCell: ({ row }) => {
          if (!row.activityStatus) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          if (row.activityStatus === ActivityStatus.Error) {
            return (
              <Typography variant="body2" color="error">
                Error
              </Typography>
            );
          }
          return (
            <Typography variant="body2">
              {prettyPrintEnumValue(row.activityStatus)}
            </Typography>
          );
        },
      },
      {
        field: "errorMessage",
        headerName: "Error Message",
        flex: 1,
        minWidth: 300,
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
            {row.errorMessage ?? "-"}
          </Typography>
        ),
      },
      {
        field: "tags",
        headerName: "Tags",
        width: 180,
        renderCell: ({ row }) => (
          <ErrorGroupWorkflowTagsCell
            workflowId={row.workflowId}
            tags={row.tags ?? []}
            linearStatusMap={linearStatusMap}
          />
        ),
      },
      {
        field: "actions",
        type: "actions",
        width: 50,
        getActions: (params) => [
          <GridActionsCellItem
            key="view"
            icon={
              <OpenInNew />
            }
            label="View Workflow"
            onClick={() => {
              if (params.row.tierIIReportId) {
                window.open(
                  `/staff/fulfillment/r/${params.row.tierIIReportId}`,
                  "_blank"
                );
              }
            }}
          />,
        ],
      },
    ],
    [linearStatusMap]
  );

  if (loading) {
    return (
      <Box sx={{ p: 2, pl: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading workflows...
        </Typography>
      </Box>
    );
  }

  return (
    <bulkActionContext.Provider
      value={{
        selectedRows: selectedRowsAsWorkflowActivities,
        dialogState,
        setDialogState,
        onClose: handleClose,
        clearSelection: handleClearSelection,
      }}
    >
      <Box sx={{ p: 2, pl: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="subtitle2">
            Workflows ({workflows.length})
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Manage Tags">
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenTagsDialog}
                  disabled={selectedWorkflows.length === 0}
                  aria-label="Manage Tags"
                  sx={{ padding: 0.5 }}
                >
                  <LocalOffer sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Create Linear Ticket">
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenLinearDialog}
                  disabled={selectedWorkflows.length === 0 || createLinearLoading}
                  aria-label="Create Linear Ticket"
                  sx={{ padding: 0.5 }}
                >
                  <LinearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Requeue">
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenRequeueDialog}
                  disabled={!hasRequeueableWorkflows}
                  aria-label="Requeue"
                  sx={{ padding: 0.5 }}
                >
                  <RestartAlt sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
        <DataGrid<WorkflowRow>
          rows={workflows}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          onRowClick={(params) => {
            if (params.row.tierIIReportId) {
              window.open(
                `/staff/fulfillment/r/${params.row.tierIIReportId}`,
                "_blank"
              );
            }
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              py: 1.5,
              alignItems: "center",
            },
            "& .MuiDataGrid-row": {
              minHeight: "52px !important",
            },
            "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='actions'] div[role='menu']":
              {
                opacity: 1,
              },
          }}
        />
      </Box>
      
      {/* Reuse existing bulk action dialogs */}
      <ManageWorkflowTagsDialogWrapper />
      <RequeueDialogWrapper />
      
      {/* Linear Ticket Dialog - reuse structure from main ErrorClustering component */}
      <ConfirmDialog
        open={linearDialogOpen}
        onClose={handleCloseLinearDialog}
        onConfirm={handleCreateLinearTicket}
        header="Create Linear Ticket"
        confirmText="Create Linear Ticket"
        cancelText="Cancel"
        loading={createLinearLoading}
        confirmStartIcon={<LinearIcon />}
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            A Linear ticket will be created in the Engineering Support project.
          </Typography>

          <TextField
            label="Title"
            value={linearTicketTitle}
            onChange={(e) => setLinearTicketTitle(e.target.value)}
            fullWidth
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={linearTicketPriority}
                onChange={(e) =>
                  setLinearTicketPriority(e.target.value as number)
                }
                label="Priority"
              >
                <MenuItem value={0}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          width: 24,
                          textAlign: "center",
                        }}
                      >
                        ---
                      </Typography>
                      <Typography>No priority</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      0
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ReportProblem fontSize="small" />
                      <Typography>Urgent</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      1
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={2}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SignalCellularAlt fontSize="small" />
                      <Typography>High</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      2
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={3}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ position: "relative", width: 24, height: 24 }}>
                        <SignalCellularAlt
                          fontSize="small"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0.18,
                          }}
                        />
                        <SignalCellularAlt2Bar
                          fontSize="small"
                          sx={{ position: "absolute", inset: 0 }}
                        />
                      </Box>
                      <Typography>Medium</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      3
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={4}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ position: "relative", width: 24, height: 24 }}>
                        <SignalCellularAlt
                          fontSize="small"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0.18,
                          }}
                        />
                        <SignalCellularAlt1Bar
                          fontSize="small"
                          sx={{ position: "absolute", inset: 0 }}
                        />
                      </Box>
                      <Typography>Low</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      4
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Status</FormLabel>
              <RadioGroup
                value={linearTicketStatus}
                onChange={(e) => setLinearTicketStatus(e.target.value)}
                row
              >
                <FormControlLabel
                  value="Triage"
                  control={<Radio />}
                  label="Triage"
                />
                <FormControlLabel
                  value="Todo"
                  control={<Radio />}
                  label="Todo"
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box
            sx={{
              bgcolor: "action.hover",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Selected Workflows
            </Typography>
            <Typography variant="body2">
              {selectedWorkflows.length} {pluralize("workflow", selectedWorkflows.length)} will be tagged with the ticket ID and "engineering".
            </Typography>
          </Box>
        </Stack>
      </ConfirmDialog>
    </bulkActionContext.Provider>
  );
});
